import React, { useState, useEffect, useRef } from 'react';
import { API_ENDPOINTS } from '../utils/apiConfig';
import { COMMON_STYLES } from '../utils/constants';

// Styles locaux
const STYLES = {
  overlay: { ...COMMON_STYLES.MODAL_OVERLAY, zIndex: 1000 },
  modal: { ...COMMON_STYLES.MODAL_CONTENT, padding: '30px', width: '100%', maxWidth: '600px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  closeBtn: (disabled) => ({ ...COMMON_STYLES.CLOSE_BUTTON, cursor: disabled ? 'not-allowed' : 'pointer' }),
  statsBox: { background: '#f8f9fa', borderRadius: '12px', padding: '20px', marginBottom: '20px' },
  statsRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '15px' },
  statItem: { textAlign: 'center' },
  statValue: (color) => ({ fontSize: '28px', fontWeight: 'bold', color }),
  statLabel: { fontSize: '13px', color: '#6c757d' },
  progressBar: { background: '#e9ecef', borderRadius: '10px', height: '20px', overflow: 'hidden' },
  progressFill: (percent) => ({ background: 'linear-gradient(135deg, #28a745, #20c997)', height: '100%', width: `${percent}%`, transition: 'width 0.3s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '12px', fontWeight: 'bold' }),
  optionBox: (type) => ({ background: type === 'danger' ? '#f8d7da' : '#fff3cd', borderRadius: '8px', padding: '12px 16px', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }),
  checkbox: { width: '18px', height: '18px', cursor: 'pointer' },
  progressBox: { background: '#e7f3ff', borderRadius: '8px', padding: '16px', marginBottom: '15px' },
  progressSmall: { background: '#c5cae9', borderRadius: '6px', height: '12px', overflow: 'hidden' },
  progressSmallFill: (percent) => ({ background: 'linear-gradient(90deg, #1976d2, #42a5f5)', height: '100%', width: `${percent}%`, transition: 'width 0.2s ease', borderRadius: '6px' }),
  currentItemInfo: { marginTop: '10px', fontSize: '13px', color: '#555' },
  batchTable: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { padding: '8px', textAlign: 'left', borderBottom: '1px solid #dee2e6' },
  td: { padding: '8px', borderBottom: '1px solid #f0f0f0' },
  btnRow: { display: 'flex', gap: '10px' },
  closeActionBtn: (disabled) => ({ flex: 1, padding: '12px', border: '2px solid #e1e5e9', borderRadius: '8px', background: 'white', cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: '600' }),
  stopBtn: { flex: 2, padding: '12px', border: 'none', borderRadius: '8px', background: '#dc3545', color: 'white', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
  runBtn: (disabled, fixIssues) => ({ flex: 2, padding: '12px', border: 'none', borderRadius: '8px', background: fixIssues ? 'linear-gradient(135deg, #dc3545, #c82333)' : disabled ? '#6c757d' : 'linear-gradient(135deg, #28a745, #20c997)', color: 'white', cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' })
};

/**
 * Modal de calcul de distance par lots avec progression en temps réel
 */
const DistanceBatchCalculator = ({ token, entityType, onClose, onComplete }) => {
  const [stats, setStats] = useState({ pending: 0, calculated: 0, issues: 0, total: 0 });
  const [isRunning, setIsRunning] = useState(false);
  const [currentBatch, setCurrentBatch] = useState([]);
  const [totalProcessed, setTotalProcessed] = useState(0);
  const [totalSuccess, setTotalSuccess] = useState(0);
  const [totalErrors, setTotalErrors] = useState(0);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [currentItem, setCurrentItem] = useState(null);
  const [revalidate, setRevalidate] = useState(false);
  const [fixIssues, setFixIssues] = useState(false);
  const [pendingValidation, setPendingValidation] = useState(0);
  const abortControllerRef = useRef(null);

  const statsUrl = entityType === 'clients'
    ? API_ENDPOINTS.CLIENTS_DISTANCE_STATS
    : API_ENDPOINTS.LOCATIONS_DISTANCE_STATS;
  const batchUrl = entityType === 'clients'
    ? API_ENDPOINTS.CLIENTS_DISTANCE_BATCH
    : API_ENDPOINTS.LOCATIONS_DISTANCE_BATCH;
  const entityLabel = entityType === 'clients' ? 'clients' : 'lieux de mise';

  // Charger les statistiques au montage
  useEffect(() => {
    loadStats();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const loadStats = async () => {
    try {
      const response = await fetch(statsUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      } else {
        setError(data.error || 'Erreur de chargement des statistiques');
      }
    } catch (err) {
      console.error('Erreur loadStats:', err);
      setError('Erreur de chargement des statistiques');
    }
  };

  const runBatch = async () => {
    setIsRunning(true);
    setError('');
    setCurrentBatch([]);
    setProgress({ current: 0, total: 0 });
    setCurrentItem(null);

    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch(batchUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ batchSize: 30, stream: true, revalidate, fixIssues }),
        signal: abortControllerRef.current.signal
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'start') {
                setProgress({ current: 0, total: data.total });
              } else if (data.type === 'progress') {
                setProgress({ current: data.current, total: data.total });
                setCurrentItem(data.item);
                setCurrentBatch(prev => [...prev, data.item]);
                if (data.item.status === 'OK') {
                  setTotalSuccess(prev => prev + 1);
                } else {
                  setTotalErrors(prev => prev + 1);
                }
                setTotalProcessed(prev => prev + 1);
              } else if (data.type === 'complete') {
                setStats(prev => ({
                  ...prev,
                  pending: data.remaining,
                  calculated: data.totalCalculated
                }));
                setCurrentItem(null);
              } else if (data.type === 'error') {
                setError(data.message);
              }
            } catch (e) {
              console.error('Erreur parsing SSE:', e);
            }
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Erreur runBatch:', err);
        setError('Erreur de connexion au serveur');
      }
    } finally {
      setIsRunning(false);
      setCurrentItem(null);
    }
  };

  const stopBatch = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const handleClose = () => {
    if (totalProcessed > 0 && onComplete) {
      onComplete();
    }
    onClose();
  };

  const progressPercent = stats.total > 0
    ? Math.round((stats.calculated / stats.total) * 100)
    : 0;

  return (
    <div style={STYLES.overlay}>
      <div style={STYLES.modal}>
        {/* Header */}
        <div style={STYLES.header}>
          <h2 style={{ margin: 0 }}>Calcul des distances - {entityLabel}</h2>
          <button onClick={handleClose} disabled={isRunning} style={STYLES.closeBtn(isRunning)}>✕</button>
        </div>

        {/* Statistiques */}
        <div style={STYLES.statsBox}>
          <div style={STYLES.statsRow}>
            <div style={STYLES.statItem}>
              <div style={STYLES.statValue('#28a745')}>{stats.calculated}</div>
              <div style={STYLES.statLabel}>Calculées</div>
            </div>
            <div style={STYLES.statItem}>
              <div style={STYLES.statValue('#ffc107')}>{stats.pending}</div>
              <div style={STYLES.statLabel}>À calculer</div>
            </div>
            {stats.issues > 0 && (
              <div style={STYLES.statItem}>
                <div style={STYLES.statValue('#dc3545')}>{stats.issues}</div>
                <div style={STYLES.statLabel}>Problèmes</div>
              </div>
            )}
            <div style={STYLES.statItem}>
              <div style={STYLES.statValue('#17a2b8')}>{stats.total}</div>
              <div style={STYLES.statLabel}>Total</div>
            </div>
          </div>
          <div style={STYLES.progressBar}>
            <div style={STYLES.progressFill(progressPercent)}>{progressPercent > 10 && `${progressPercent}%`}</div>
          </div>
        </div>

        {stats.issues > 0 && !isRunning && (
          <div style={STYLES.optionBox('danger')}>
            <input type="checkbox" id="fixIssues" checked={fixIssues} onChange={(e) => { setFixIssues(e.target.checked); if (e.target.checked) setRevalidate(false); }} style={STYLES.checkbox} />
            <label htmlFor="fixIssues" style={{ cursor: 'pointer', fontSize: '14px', color: '#721c24' }}>
              <strong>Corriger les problèmes</strong> - Recalculer les {stats.issues} adresses en erreur
            </label>
          </div>
        )}

        {stats.calculated > 0 && !isRunning && !fixIssues && (
          <div style={STYLES.optionBox('warning')}>
            <input type="checkbox" id="revalidate" checked={revalidate} onChange={(e) => setRevalidate(e.target.checked)} style={STYLES.checkbox} />
            <label htmlFor="revalidate" style={{ cursor: 'pointer', fontSize: '14px', color: '#856404' }}>
              <strong>Revalider les adresses</strong> - Recalculer les {stats.calculated} déjà faits (IGN vs ORS)
            </label>
          </div>
        )}

        {isRunning && progress.total > 0 && (
          <div style={STYLES.progressBox}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontWeight: '600', color: '#1976d2' }}>Calcul en cours... {progress.current}/{progress.total}</span>
              <span style={{ color: '#666' }}>{Math.round((progress.current / progress.total) * 100)}%</span>
            </div>
            <div style={STYLES.progressSmall}>
              <div style={STYLES.progressSmallFill((progress.current / progress.total) * 100)} />
            </div>
            {currentItem && (
              <div style={STYLES.currentItemInfo}>
                <span style={{ marginRight: '8px' }}>{currentItem.status === 'OK' ? '✓' : currentItem.status === 'WARNING' ? '⚠' : '✗'}</span>
                <strong>{currentItem.code}</strong> - {currentItem.name?.substring(0, 25)}
                {currentItem.distance_km && <span style={{ marginLeft: '8px', color: '#28a745', fontWeight: 'bold' }}>{currentItem.distance_km} km</span>}
                {currentItem.geocoding_status === 'warning' && <span style={{ marginLeft: '8px', color: '#ffc107', fontWeight: 'bold' }}>À vérifier</span>}
                {currentItem.geocoding_status === 'error' && <span style={{ marginLeft: '8px', color: '#dc3545', fontWeight: 'bold' }}>Adresse invalide</span>}
              </div>
            )}
          </div>
        )}

        {error && <div style={{ ...COMMON_STYLES.ALERT, ...COMMON_STYLES.ALERT_DANGER, marginBottom: '15px' }}>{error}</div>}

        {totalProcessed > 0 && !isRunning && (
          <div style={{ ...COMMON_STYLES.ALERT, ...COMMON_STYLES.ALERT_SUCCESS, marginBottom: '15px' }}>
            <strong>Dernier batch:</strong> {totalProcessed} traités, {totalSuccess} réussis, {totalErrors} erreurs
          </div>
        )}

        {currentBatch.length > 0 && (
          <div style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid #e9ecef', borderRadius: '8px', marginBottom: '20px' }}>
            <table style={STYLES.batchTable}>
              <thead>
                <tr style={{ background: '#f8f9fa', position: 'sticky', top: 0 }}>
                  <th style={STYLES.th}>Code</th>
                  <th style={STYLES.th}>Nom</th>
                  <th style={{ ...STYLES.th, textAlign: 'right' }}>Distance</th>
                </tr>
              </thead>
              <tbody>
                {currentBatch.map((item, idx) => {
                  const rowBg = !item.distance_km || item.geocoding_status === 'error' ? '#f8d7da' : item.geocoding_status === 'warning' ? '#fff3cd' : 'white';
                  const distColor = item.geocoding_status === 'error' ? '#dc3545' : item.geocoding_status === 'warning' ? '#856404' : '#28a745';
                  return (
                    <tr key={idx} style={{ background: rowBg }}>
                      <td style={STYLES.td}>{item.code}</td>
                      <td style={STYLES.td}>{item.name?.substring(0, 30)}{item.name?.length > 30 ? '...' : ''}</td>
                      <td style={{ ...STYLES.td, textAlign: 'right' }}>
                        {item.distance_km ? (
                          <span style={{ color: distColor, fontWeight: 'bold' }}>{item.distance_km} km{item.geocoding_status === 'error' && ' ⚠'}{item.geocoding_status === 'warning' && ' ?'}</span>
                        ) : (
                          <span style={{ color: '#dc3545', fontSize: '11px' }}>Échec calcul</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div style={STYLES.btnRow}>
          <button onClick={handleClose} disabled={isRunning} style={STYLES.closeActionBtn(isRunning)}>Fermer</button>
          {isRunning ? (
            <button onClick={stopBatch} style={STYLES.stopBtn}>Arrêter le calcul</button>
          ) : (
            <button onClick={runBatch} disabled={!fixIssues && !revalidate && stats.pending === 0} style={STYLES.runBtn(!fixIssues && !revalidate && stats.pending === 0, fixIssues)}>
              {fixIssues ? `Corriger ${Math.min(30, stats.issues)} problèmes (${stats.issues} total)` :
               revalidate ? `Revalider 30 fiches (${stats.calculated} total)` :
               stats.pending === 0 ? 'Toutes les distances sont calculées' :
               `Calculer 30 fiches (${stats.pending} restantes)`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DistanceBatchCalculator;
